
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { createAtlasIframeService, AtlasIframeConfig } from '@/services/atlasIframeService';
import { StoreModel } from '@/models/store';
import { setFeatureVersion, featureForClassicPath } from '@/config/atlasFeatures';
import { EventHandler } from '@/modules/events';
import { EventBus, LOGOUT_REQUESTED } from '@/eventBus';
import { PURCHASE_LIST_UPDATED } from '@/collections/purchase_list';
import { buildAtlasContext, classifyAtlasParentNavigate } from './atlasEmbedContext';
import { AtlasSoftNavController, toWarblerPath, type AtlasNavTelemetry } from './atlasSoftNav';
import { redirectApp, redirectExternal } from '@/utils/shopifyBridge';
import { isSameAppPath } from '@/utils/appPath';

@Component({
  components: {},
})
export default class AtlasEmbed extends Vue {
  @Prop({ default: () => new StoreModel() })
  public store!: StoreModel;

  @Prop({ default: '' })
  public agent!: string;

  @Prop({ default: '' })
  public tab!: string;

  private iframeService = createAtlasIframeService();
  public loading = true;
  public error: string | null = null;
  public iframeSrc = '';

  /**
   * True while an acknowledged soft navigation is still waiting for Atlas to
   * echo the committed route (set by the controller after its 200 ms grace).
   * Separate from `loading` so the boot/hard-reload path is untouched.
   */
  public navPending = false;

  private atlasReady = false;

  /**
   * Owns dispatch / echo-suppression / ACK-fallback. Replaces the old
   * `tabChangeFromAtlas` boolean, which guarded only one hop and could not
   * cover agent changes. See atlasSoftNav.ts.
   */
  private softNav = new AtlasSoftNavController({
    postNavigate: (path, query) => this.iframeService.navigateTo(path, query),
    hardReload: () => this.hardReloadIframe(),
    setTimer: (fn, ms) => window.setTimeout(fn, ms),
    clearTimer: (id) => window.clearTimeout(id),
    // Route through a method, like the other deps: class-field initializers
    // run against vue-class-component's temporary data object, so assigning
    // `this.navPending` directly from this arrow would never reach the
    // reactive instance. Methods are bound to the real vm.
    setPending: (pending) => this.setNavPending(pending),
    onTelemetry: (event) => this.trackNavigation(event),
  });

  /**
   * Get the base URL for the Atlas frontend
   */
  get atlasBaseUrl(): string {
    return process.env.VUE_APP_ATLAS_FRONTEND_URL || 'http://localhost:3000';
  }

  /**
   * Build the initial iframe URL from the agent prop and current query params.
   * Called once at creation — subsequent query param changes from Atlas
   * only update Warbler's URL bar, not the iframe src.
   */
  private buildIframeSrc(): string {
    let url = this.atlasBaseUrl;
    if (this.agent) {
      url = `${this.atlasBaseUrl}/${this.agent}`;
    }
    // Append tab path segment for setup deep linking (e.g. /setup/variants)
    if (this.tab) {
      url += `/${this.tab}`;
    }
    // Forward query params from Warbler's URL to the iframe for deep linking.
    // Shares currentQueryString() with the soft-navigation payload on purpose:
    // the iframe src and the NAVIGATE message must carry the identical query
    // string, and two copies of the serialization rule would drift.
    return `${url}${this.currentQueryString()}`;
  }

  /** The query string currently in Warbler's URL, forwarded on navigation. */
  private currentQueryString(): string {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(this.$route.query)) {
      if (Array.isArray(value)) {
        for (const item of value) {
          if (typeof item === 'string') params.append(key, item);
        }
      } else if (typeof value === 'string') {
        params.set(key, value);
      }
    }
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  }

  /**
   * Rebuild the iframe src from scratch — the original navigation mechanism,
   * now reserved for first mount and for the fallback when Atlas does not
   * acknowledge a soft navigation.
   */
  private hardReloadIframe(): void {
    this.loading = true;
    this.error = null;
    this.iframeSrc = '';
    this.$nextTick(() => {
      this.iframeSrc = this.buildIframeSrc();
    });
  }

  created(): void {
    this.iframeSrc = this.buildIframeSrc();
    // The iframe starts on this destination, so a watcher firing for an
    // unchanged route must not dispatch a redundant navigation.
    this.softNav.seed(toWarblerPath(this.agent, this.tab));
  }

  /**
   * Watch for agent changes (a cross-section navigation, e.g. Create PO from
   * the supplier list: /assistant/suppliers → /assistant/buying-table?supplierId=…).
   *
   * Historically this blanked and rebuilt iframeSrc, which reloaded the iframe
   * and rebooted the entire Atlas app — losing the agent conversation Atlas
   * mounts CopilotKit at its root specifically to preserve. It now asks Atlas
   * to route itself; the controller falls back to the old rebuild if Atlas
   * does not acknowledge.
   */
  @Watch('agent')
  onAgentChange(newAgent: string): void {
    if (!newAgent) return;
    this.dispatchSoftNavigation();
  }

  /**
   * Watch for tab changes (e.g. a different settings item in the menu). Same
   * soft-navigation path as onAgentChange; echo suppression lives in the
   * controller rather than a one-hop flag, so an Atlas-initiated navigation
   * arriving back here is not bounced straight out again.
   */
  @Watch('tab')
  onTabChange(): void {
    this.dispatchSoftNavigation();
  }

  /**
   * Ask Atlas to navigate. Falls back to a hard reload when Atlas has not yet
   * signalled READY — there is no live subscriber to accept the message.
   */
  private dispatchSoftNavigation(): void {
    if (!this.atlasReady) {
      this.hardReloadIframe();
      this.softNav.seed(toWarblerPath(this.agent, this.tab));
      return;
    }
    this.softNav.requestNavigation(this.agent, this.tab, this.currentQueryString());
  }

  private loadingTimeout: number | null = null;

  /**
   * Initialize the iframe service when mounted
   */
  public mounted(): void {
    const iframe = this.$refs.atlasFrame as HTMLIFrameElement;
    if (iframe) {
      this.iframeService.updateConfig({
        baseUrl: this.atlasBaseUrl,
        onReady: this.onAtlasReady.bind(this),
        onNavigation: this.onAtlasNavigation.bind(this),
        onActionComplete: this.onAtlasActionComplete.bind(this),
        onParentNavigate: this.onAtlasParentNavigate.bind(this),
        onQueryParams: this.onAtlasQueryParams.bind(this),
        onPurchaseListChanged: this.onAtlasPurchaseListChanged.bind(this),
        onNavigateAck: this.onAtlasNavigateAck.bind(this),
        onOpenLiveChat: this.openCrispForAtlasHandoff.bind(this),
        onRedirectExternal: this.onAtlasRedirectExternal.bind(this),
      });
      this.iframeService.initialize(iframe);

      // Fallback: hide loading after 5 seconds if no READY message received
      this.loadingTimeout = window.setTimeout(() => {
        if (this.loading) {
          console.warn('[AtlasEmbed] No READY message received, hiding loading anyway');
          this.loading = false;
        }
      }, 5000);
    }
  }

  /**
   * Clean up when component is destroyed
   */
  public beforeDestroy(): void {
    this.iframeService.destroy();
    this.softNav.dispose();
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
    }
  }

  /**
   * Handle iframe load event
   */
  public onIframeLoad(): void {
    // Iframe loaded - give a short delay then hide loading as fallback
    setTimeout(() => {
      if (this.loading) {
        this.loading = false;
      }
    }, 1000);
  }

  /**
   * Handle iframe error event
   */
  public onIframeError(): void {
    this.loading = false;
    this.error = 'Failed to load Atlas. Please check your connection and try again.';
  }

  /**
   * Handle Atlas frontend ready message
   */
  private onAtlasReady(): void {
    this.loading = false;
    this.error = null;
    this.atlasReady = true;

    // Send auth token
    this.iframeService.sendAuthToken();

    // Send store context (e.g. package tier awareness) so Atlas can gate
    // package-specific UI without its own entitlement fetch.
    this.sendStoreContext();
  }

  /**
   * Send the current store context (package tier + first-import state) to
   * Atlas via CONTEXT_UPDATE. Atlas overwrites its context slot on every
   * message, so re-sending after the store finishes loading is safe.
   *
   * Always routed through buildAtlasContext() so athenaFinished can never be
   * dropped from a send: Atlas treats an ABSENT athenaFinished as "import
   * complete" (fail-safe), so a send missing the field would silently mask a
   * genuinely-importing store (US-TODAY-101).
   */
  private sendStoreContext(): void {
    this.iframeService.sendContext(buildAtlasContext(this.store));
  }

  /**
   * The store often finishes its async fetch after the iframe's READY
   * message — onAtlasReady's context send would then latch packageName=null
   * (tier 'free') for the whole session. Re-send once the real store lands.
   * Guarded to only fire once the iframe is ready (a READY has already been
   * handled) and only on an actual store swap (not the initial empty ->
   * empty no-op).
   */
  @Watch('store.id')
  onStoreIdChange(newId: number | null | undefined, oldId: number | null | undefined): void {
    if (!this.atlasReady) return;
    if (newId == null || newId === oldId) return;
    this.sendStoreContext();
  }

  /**
   * The package name can also arrive later on its own — via
   * currentPackage.getCurrent() resolving after store.id is already set —
   * without store.id itself changing, so onStoreIdChange never fires. Guard
   * identically: only once the iframe is ready, and only on an actual change.
   */
  @Watch('store.currentPackage.name')
  onPackageNameChange(newName: string | undefined, oldName: string | undefined): void {
    if (!this.atlasReady) return;
    if (newName == null || newName === oldName) return;
    this.sendStoreContext();
  }

  /**
   * The store's first-import status flips IN PLACE on the same StoreModel:
   * App.vue's checkAthenaFinishInterval polls every 60s and sets
   * store.isAthenaFinished = true once the import completes. Neither
   * store.id nor store.currentPackage.name changes when that happens, so
   * without this watcher Atlas is never told the import finished and the
   * merchant is stranded on its import-wait home until a manual reload
   * (US-TODAY-101 review Critical-2). Guarded identically to the other
   * watchers: only once the iframe is ready, and only on an actual change.
   */
  @Watch('store.isAthenaFinished')
  onAthenaFinishedChange(newValue: boolean | undefined, oldValue: boolean | undefined): void {
    if (!this.atlasReady) return;
    if (newValue === oldValue) return;
    this.sendStoreContext();
  }

  /**
   * Handle navigation changes from Atlas
   */
  private onAtlasNavigation(route: string): void {
    // Navigate to the requested route in Warbler
    // This handles both Warbler routes (e.g., /buying-table) and
    // Atlas iframe routes (e.g., /assistant/forecasting)
    const targetPath = route.startsWith('/') ? route : `/${route}`;

    // Record the destination BEFORE touching the router. The router update
    // changes the agent/tab props, which fires the watchers — without this,
    // Warbler would dispatch this very navigation back to Atlas, which would
    // report it again, and so on.
    // noteAtlasNavigation is also the arrival signal for a pending soft
    // navigation: it hides the overlay and reports `soft` (spec D1/D5).
    this.softNav.noteAtlasNavigation(targetPath);

    if (this.$route.path !== targetPath) {
      // Use replace for tab changes within the same agent to avoid polluting history
      const sameAgentTarget = this.agent && (
        targetPath === `/assistant/${this.agent}` ||
        targetPath.startsWith(`/assistant/${this.agent}/`)
      );
      if (sameAgentTarget) {
        this.$router.replace(targetPath).catch(() => {
          // Ignore navigation duplicates
        });
      } else {
        this.$router.push(targetPath);
      }
    }
  }

  /**
   * Atlas accepted a soft navigation — cancel the hard-reload fallback and
   * start waiting for the NAVIGATION echo (the controller owns the timers).
   */
  private onAtlasNavigateAck(path: string): void {
    this.softNav.handleAck(path);
  }

  /** Controller callback: show/hide the overlay for a pending soft navigation. */
  private setNavPending(pending: boolean): void {
    this.navPending = pending;
  }

  /**
   * Report how an Atlas-to-Atlas transition resolved. A rising `fallback` rate
   * means version skew between the Warbler and Atlas deploys, or a path
   * Warbler believes Atlas serves and it does not (see arch.md § 8).
   */
  private trackNavigation(event: AtlasNavTelemetry): void {
    const eventHandler = new EventHandler({ store: this.store });
    eventHandler.track('atlas_iframe_navigation', {
      outcome: event.outcome,
      atlas_path: event.path,
      latency_ms: event.latencyMs,
      store_id: this.store?.id,
    });
  }

  /**
   * Handle parent navigation requests from Atlas.
   * Navigates Warbler's own router (no /atlas prefix).
   */
  private onAtlasParentNavigate(
    path: string,
    switchToClassic = false,
    opts: { reload?: boolean } = {},
  ): void {
    // `/logout` is a menu SENTINEL, not a route (see classifyAtlasParentNavigate).
    // Atlas's settings sidebar sends it as an ordinary parentPath, so it has to be
    // intercepted here exactly as VerticalMenuBar.handleMenuClick intercepts it —
    // otherwise the router's `*` wildcard redirects to `/` and the user lands on
    // Home with the session still live (AS-10880). Checked BEFORE the
    // switchToClassic block so logging out can never also downgrade a feature,
    // and before `$router.push` so the wildcard is never reached.
    if (classifyAtlasParentNavigate(path).kind === 'logout') {
      EventBus.$emit(LOGOUT_REQUESTED);
      return;
    }

    // Downgrade a feature to classic ONLY on the explicit "Switch back to
    // classic view" links (Atlas ClassicSwitchBanner sends switchToClassic).
    // This used to run for EVERY parent navigation into a mapped path
    // (user-reported 2026-07-06): an ordinary Settings-hub click on
    // "Automated POs" silently flipped automatedPO to classic and landed on
    // the classic page even for a user who had chosen atlas. Ordinary
    // navigations are version-neutral — the router's atlas-redirect guard
    // (getAtlasRedirectTarget) resolves the destination per the user's saved
    // preference.
    if (switchToClassic) {
      // Atlas iframe has separate localStorage, so Warbler must set its own.
      // Detect which feature the target path belongs to and switch it to classic
      // (shared pure resolver — also knows the regex-only families such as
      // /my-reports/:id, classic-switch spec D6).
      const feature = featureForClassicPath(path);
      if (feature) {
        setFeatureVersion(feature, 'classic');
        // Track the switch-back event from Warbler (not Atlas) because
        // the Atlas iframe is about to be destroyed by the navigation below.
        const eventHandler = new EventHandler({ store: this.store });
        eventHandler.track('feature_version_switched', {
          feature_name: feature,
          from_version: 'atlas',
          to_version: 'classic',
          store_id: this.store.id,
        });
      }
    }
    // AS-10885: a full document load, not a router push. Atlas sets `reload`
    // after the merchant's package changes (subscription confirmed, or the
    // downgrade to Free). This app fetches the store once at bootstrap and the
    // $route watcher does NOT refetch it, so a soft navigation here leaves the
    // menus and entitlements on the OLD plan — the merchant has just paid and
    // still sees locked features. redirectApp is the same primitive classic's
    // SubscriptionConfirm.vue used to land on this page.
    if (opts.reload) {
      // `path` is untrusted postMessage data, and this branch does NOT go
      // through $router.push -- redirectApp is window.location.assign (or
      // open(path, '_self') embedded), which will follow an absolute URL
      // off-origin and will execute a `javascript:` URL. The
      // PARENT_REDIRECT_EXTERNAL allowlist does not cover this message type.
      if (!isSameAppPath(path)) {
        console.warn('[AtlasEmbed] refused reload navigation to a non-app path:', path);
        return;
      }
      redirectApp(path);
      return;
    }

    this.$router.push(path).catch(() => {
      // Ignore navigation duplicates
    });
  }

  /**
   * Handle query parameter updates from Atlas.
   * Uses Vue Router replace so the $route watcher in App.vue fires,
   * which in turn calls Shopify App Bridge History.REPLACE when embedded.
   */
  private onAtlasQueryParams(queryString: string): void {
    // Parse the incoming query string into a scalar object for Vue Router.
    // Last value wins for duplicate keys to preserve the scalar contract
    // expected by downstream consumers (ReportPage, HeaderTitle, etc.).
    const params = new URLSearchParams(queryString || '');
    const query: Record<string, string> = {};
    for (const [key, value] of params.entries()) {
      query[key] = value;
    }

    // Use router.replace to trigger the $route watcher in App.vue,
    // which handles Shopify App Bridge History.REPLACE for embedded mode
    this.$router.replace({ query }).catch(() => {
      // Ignore NavigationDuplicated (same query)
    });
  }

  /**
   * Handle action completion from Atlas
   */
  private onAtlasActionComplete(action: string, result: unknown): void {
    // Emit event for parent components if needed
    this.$emit('atlas-action', { action, result });
  }

  /**
   * Handle a purchase-list change reported by Atlas. Re-emits Warbler's
   * existing PURCHASE_LIST_UPDATED event so PurchaseListIcon refreshes its
   * badge count from /purchase-list/count.
   */
  private onAtlasPurchaseListChanged(): void {
    EventBus.$emit(PURCHASE_LIST_UPDATED);
  }

  /**
   * Leave the app for an external Shopify URL on Atlas's behalf — the
   * charge-approval `confirmationUrl` (AS-10881).
   *
   * Atlas is an iframe inside this app inside the Shopify admin, so it cannot
   * reach admin.shopify.com itself; only the parent holds App Bridge. The URL
   * arrives already validated against the Shopify allowlist — AtlasIframeService
   * drops anything else before this runs — so there is nothing to re-check here.
   */
  private onAtlasRedirectExternal(url: string): void {
    redirectExternal(url);
  }

  /**
   * Handle an OPEN_LIVE_CHAT handoff request from Atlas — open the Crisp
   * widget with support-operator context. Mirrors ChatConversation.vue's
   * openLiveChat() sequence (chat:show, chat:open, session:data, fallback
   * snackbar); session-data keys match Atlas's own standalone Crisp handoff
   * (frontend/src/config/crisp.ts) verbatim so support sees one consistent
   * record regardless of which surface the merchant handed off from.
   */
  public openCrispForAtlasHandoff(payload: {
    ticketKey?: string;
    reason?: string;
    shop?: string;
    message?: string;
  }): void {
    if (!(window as any).$crisp) {
      EventBus.$emit('show-snackbar', {
        message: 'Live chat is not available at the moment.',
        color: 'error',
        timeout: 5000,
      });
      return;
    }
    (window as any).$crisp.push(['do', 'chat:show']);
    (window as any).$crisp.push(['do', 'chat:open']);
    (window as any).$crisp.push(['set', 'session:data', [[
      ['ai_handoff', 'true'],
      ['source', 'atlas-agent'],
      ...(payload.ticketKey ? [['ticket_key', payload.ticketKey]] : []),
      ...(payload.reason ? [['handoff_reason', payload.reason]] : []),
      ...(payload.shop ? [['shop', payload.shop]] : []),
    ]]]);

    // AS-10883: send the merchant's own text as the opening message. The
    // downgrade funnel promises a reply about a gift card, so the reason,
    // details and contact they just typed have to land in the conversation --
    // an empty widget loses them. Matches classic's
    // `$crisp.do('message:send', ['text', ...])`. Sent AFTER session:data so
    // support sees the context alongside the message.
    if (payload.message) {
      (window as any).$crisp.push(['do', 'message:send', ['text', payload.message]]);
    }
  }

  /**
   * Reload the iframe
   */
  public reload(): void {
    this.hardReloadIframe();
    this.softNav.seed(toWarblerPath(this.agent, this.tab));
  }
}
